import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import api, { formatError, formatDoctorName } from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { Plus, Search, Printer, Trash2, ClipboardList, X, Pill } from "lucide-react";

const FREQUENCIES = ["Once daily", "Twice daily", "3 times/day", "4 times/day", "Every 8 hours", "At bedtime", "As needed", "With meals"];
const DURATIONS = ["1 day", "3 days", "5 days", "7 days", "10 days", "14 days", "1 month", "3 months", "Ongoing"];
const EMPTY_MED = { name: "", dosage: "", frequency: "Twice daily", duration: "7 days", instructions: "" };

export default function Prescriptions() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { toast } = useToast();
  const [prescriptions, setPrescriptions] = useState([]);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [viewRx, setViewRx] = useState(null);
  const [saving, setSaving] = useState(false);
  const [myDoctor, setMyDoctor] = useState(null);
  const [form, setForm] = useState({
    patient_id: "", doctor_id: "", appointment_id: "",
    diagnosis: "", medicines: [{ ...EMPTY_MED }],
    instructions: "", follow_up_date: ""
  });
  const [medSearch, setMedSearch] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [pRes, dRes, invRes, rxRes] = await Promise.all([
        api.get("/patients"), api.get("/doctors"), api.get("/inventory"),
        api.get("/prescriptions"),
      ]);
      setPatients(pRes.data);
      setDoctors(dRes.data);
      setInventory(invRes.data.filter(i => i.category === "medicine" || i.category === "consumables"));
      setPrescriptions(rxRes.data);
      // Find my doctor profile (for doctor role)
      if (user?.role === "doctor") {
        const me = dRes.data.find(d => d.email === user.email);
        if (me) {
          setMyDoctor(me);
          setForm(f => ({ ...f, doctor_id: me.id }));
          const apptRes = await api.get(`/appointments?doctor_id=${me.id}`);
          setAppointments(apptRes.data.data.filter(a => a.status !== "cancelled"));
        }
      } else {
        const apptRes = await api.get("/appointments");
        setAppointments(apptRes.data.data);
      }
    } catch (err) {
      toast({ title: "Error", description: formatError(err), variant: "destructive" });
    } finally { setLoading(false); }
  }, [toast, user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openNew = () => {
    setForm({
      patient_id: "", doctor_id: myDoctor?.id || "",
      appointment_id: "", diagnosis: "",
      medicines: [{ ...EMPTY_MED }], instructions: "", follow_up_date: ""
    });
    setShowModal(true);
  };

  const addMedicine = () => setForm({ ...form, medicines: [...form.medicines, { ...EMPTY_MED }] });
  const removeMedicine = (idx) => setForm({ ...form, medicines: form.medicines.filter((_, i) => i !== idx) });
  const updateMed = (idx, field, val) => {
    const meds = [...form.medicines];
    meds[idx] = { ...meds[idx], [field]: val };
    setForm({ ...form, medicines: meds });
  };

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.appointment_id) delete payload.appointment_id;
      const { data } = await api.post("/prescriptions", payload);
      toast({ title: "Prescription created", description: `${data.rx_number}` });
      setShowModal(false);
      fetchData();
    } catch (err) {
      toast({ title: "Error", description: formatError(err), variant: "destructive" });
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this prescription?")) return;
    try {
      await api.delete(`/prescriptions/${id}`);
      toast({ title: "Deleted" });
      fetchData();
    } catch (err) {
      toast({ title: "Error", description: formatError(err), variant: "destructive" });
    }
  };

  const printPrescription = (rx) => {
    const w = window.open("", "_blank", "width=700,height=900");
    w.document.write(`
      <!DOCTYPE html><html><head>
      <title>Rx - ${rx.rx_number}</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 40px; color: #111; max-width: 650px; margin: 0 auto; }
        .header { display: flex; justify-content: space-between; border-bottom: 2px solid #059669; padding-bottom: 16px; }
        .clinic { font-size: 20px; font-weight: 800; color: #059669; }
        .doctor { text-align: right; font-size: 13px; }
        .doctor-name { font-weight: 700; font-size: 15px; }
        .patient-row { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px; margin: 20px 0; background:#f0fdf4; padding: 12px; border-radius: 8px; font-size: 13px; }
        .label { color: #666; font-size: 11px; }
        .rx-symbol { font-size: 40px; color: #059669; font-weight: 800; margin: 16px 0 8px; font-family: serif; }
        .diagnosis { font-size: 13px; color: #374151; margin-bottom: 16px; }
        .med-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
        .med-table th { background: #f0fdf4; padding: 8px 12px; font-size: 12px; text-align: left; border-bottom: 1px solid #d1fae5; }
        .med-table td { padding: 8px 12px; font-size: 13px; border-bottom: 1px solid #f8fafc; }
        .instructions { background: #fffbeb; border-left: 3px solid #f59e0b; padding: 10px 14px; font-size: 13px; margin-bottom: 16px; }
        .followup { font-size: 13px; color: #059669; font-weight: 600; margin-bottom: 20px; }
        .signature { text-align: right; margin-top: 40px; font-size: 13px; }
        @media print { @page { margin: 15mm; } }
      </style></head><body>
      <div class="header">
        <div><div class="clinic">MedLif Clinic</div><div style="font-size:12px;color:#666">Quality Healthcare</div></div>
        <div class="doctor">
          <div class="doctor-name">${formatDoctorName(rx.doctor_name)}</div>
          <div>${rx.specialization || "General Medicine"}</div>
          <div style="color:#059669">${rx.rx_number}</div>
        </div>
      </div>
      <div class="patient-row">
        <div><div class="label">Patient</div><strong>${rx.patient_name}</strong></div>
        <div><div class="label">Date</div><strong>${rx.created_at?.split("T")[0]}</strong></div>
        <div><div class="label">Ref No.</div><strong>${rx.rx_number}</strong></div>
      </div>
      <div style="font-size:13px;color:#374151;margin-bottom:8px;"><strong>Diagnosis:</strong> ${rx.diagnosis}</div>
      <div class="rx-symbol">&#x211E;</div>
      <table class="med-table">
        <thead><tr><th>#</th><th>Medicine</th><th>Dosage</th><th>Frequency</th><th>Duration</th><th>Instructions</th></tr></thead>
        <tbody>
          ${rx.medicines?.map((m, i) => `<tr>
            <td>${i + 1}</td>
            <td><strong>${m.name}</strong></td>
            <td>${m.dosage}</td>
            <td>${m.frequency}</td>
            <td>${m.duration}</td>
            <td>${m.instructions || "—"}</td>
          </tr>`).join("") || ""}
        </tbody>
      </table>
      ${rx.instructions ? `<div class="instructions"><strong>Instructions:</strong> ${rx.instructions}</div>` : ""}
      ${rx.follow_up_date ? `<div class="followup">Follow-up: ${rx.follow_up_date}</div>` : ""}
      <div class="signature">
        <div style="border-top:1px solid #ccc;display:inline-block;padding-top:8px;min-width:160px;text-align:center">
          <div style="font-weight:700">${formatDoctorName(rx.doctor_name)}</div>
          <div style="font-size:11px;color:#666">${rx.specialization || "General Medicine"}</div>
        </div>
      </div>
      <script>window.onload = () => { window.print(); setTimeout(() => window.close(), 500); }</script>
      </body></html>
    `);
    w.document.close();
  };

  const getMedSuggestions = (query) => {
    if (!query || query.length < 2) return [];
    return inventory.filter(i => i.name.toLowerCase().includes(query.toLowerCase())).slice(0, 5);
  };

  const filtered = prescriptions.filter(rx =>
    rx.patient_name?.toLowerCase().includes(search.toLowerCase()) ||
    rx.rx_number?.toLowerCase().includes(search.toLowerCase()) ||
    rx.diagnosis?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-sans">Prescriptions</h1>
          <p className="text-slate-500 text-sm mt-1">Write and manage patient prescriptions</p>
        </div>
        <Button className="bg-emerald-700 hover:bg-emerald-800 text-white gap-2" onClick={openNew} data-testid="new-rx-btn">
          <Plus size={16} /> Write Prescription
        </Button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="relative max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search prescriptions..." className="pl-9 border-slate-200" value={search} onChange={e => setSearch(e.target.value)} data-testid="rx-search" />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="font-semibold text-slate-700">Rx Number</TableHead>
              <TableHead className="font-semibold text-slate-700">Patient</TableHead>
              <TableHead className="font-semibold text-slate-700">Doctor</TableHead>
              <TableHead className="font-semibold text-slate-700">Diagnosis</TableHead>
              <TableHead className="font-semibold text-slate-700">Medicines</TableHead>
              <TableHead className="font-semibold text-slate-700">Date</TableHead>
              <TableHead className="font-semibold text-slate-700">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-12 text-slate-400">Loading...</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-12 text-slate-400">
                <ClipboardList size={32} className="mx-auto mb-2 opacity-30" /><p>No prescriptions found</p>
              </TableCell></TableRow>
            ) : filtered.map(rx => (
              <TableRow key={rx.id} className="table-row-hover" data-testid={`rx-row-${rx.id}`}>
                <TableCell><span className="font-mono text-xs text-emerald-700 font-semibold">{rx.rx_number}</span></TableCell>
                <TableCell className="font-medium text-slate-900 text-sm">{rx.patient_name}</TableCell>
                <TableCell className="text-sm text-slate-600">{formatDoctorName(rx.doctor_name)}</TableCell>
                <TableCell className="text-sm text-slate-700 max-w-36 truncate">{rx.diagnosis}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {rx.medicines?.slice(0, 2).map((m, i) => (
                      <span key={i} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{m.name}</span>
                    ))}
                    {rx.medicines?.length > 2 && <span className="text-xs text-slate-400">+{rx.medicines.length - 2}</span>}
                  </div>
                </TableCell>
                <TableCell className="text-xs text-slate-500">{rx.created_at?.split("T")[0]}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-500 hover:text-blue-600" onClick={() => setViewRx(rx)} data-testid={`view-rx-${rx.id}`}>
                      <ClipboardList size={14} />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-500 hover:text-emerald-600" onClick={() => printPrescription(rx)} data-testid={`print-rx-${rx.id}`}>
                      <Printer size={14} />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-500 hover:text-rose-600" onClick={() => handleDelete(rx.id)} data-testid={`delete-rx-${rx.id}`}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Write Prescription Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-sans flex items-center gap-2">
              <Pill size={18} className="text-emerald-600" /> Write Prescription
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-5 mt-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-slate-700">Patient <span className="text-rose-500">*</span></Label>
                <select value={form.patient_id} onChange={e => setForm({ ...form, patient_id: e.target.value })} className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white" required data-testid="rx-patient-select">
                  <option value="">Select Patient</option>
                  {patients.map(p => <option key={p.id} value={p.id}>{p.name} ({p.patient_id})</option>)}
                </select>
              </div>
              <div>
                <Label className="text-sm text-slate-700">Doctor <span className="text-rose-500">*</span></Label>
                <select value={form.doctor_id} onChange={e => setForm({ ...form, doctor_id: e.target.value })} className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white" required disabled={!!myDoctor}>
                  <option value="">Select Doctor</option>
                  {doctors.map(d => <option key={d.id} value={d.id}>{formatDoctorName(d.name)}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-sm text-slate-700">Linked Appointment (optional)</Label>
                <select value={form.appointment_id} onChange={e => setForm({ ...form, appointment_id: e.target.value })} className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white">
                  <option value="">None</option>
                  {appointments.filter(a => !form.patient_id || a.patient_id === form.patient_id).map(a => <option key={a.id} value={a.id}>#{a.token_number} — {a.patient_name} ({a.visit_date?.split("T")[0]})</option>)}
                </select>
              </div>
              <div>
                <Label className="text-sm text-slate-700">Follow-up Date</Label>
                <Input type="date" value={form.follow_up_date} onChange={e => setForm({ ...form, follow_up_date: e.target.value })} className="mt-1 border-slate-200" />
              </div>
              <div className="col-span-2">
                <Label className="text-sm text-slate-700">Diagnosis <span className="text-rose-500">*</span></Label>
                <Input value={form.diagnosis} onChange={e => setForm({ ...form, diagnosis: e.target.value })} placeholder="Primary diagnosis" className="mt-1 border-slate-200" required data-testid="rx-diagnosis" />
              </div>
            </div>

            {/* Medicines */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-sm font-semibold text-slate-700">Medicines</Label>
                <Button type="button" variant="outline" size="sm" onClick={addMedicine} className="text-xs gap-1" data-testid="add-med-btn">
                  <Plus size={12} /> Add Medicine
                </Button>
              </div>
              <div className="space-y-3">
                {form.medicines.map((med, idx) => (
                  <div key={idx} className="bg-slate-50 rounded-xl p-4 relative">
                    <button type="button" onClick={() => removeMedicine(idx)} className="absolute top-3 right-3 text-slate-400 hover:text-rose-500 w-6 h-6 flex items-center justify-center rounded-full hover:bg-rose-50">
                      <X size={14} />
                    </button>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      <div className="col-span-2 sm:col-span-3">
                        <Label className="text-xs text-slate-500">Medicine Name <span className="text-rose-400">*</span></Label>
                        <div className="relative">
                          <Input
                            value={med.name}
                            onChange={e => { updateMed(idx, "name", e.target.value); setMedSearch({ idx, q: e.target.value }); }}
                            placeholder="Type medicine name..."
                            className="mt-1 border-slate-200 text-sm"
                            required
                            list={`med-list-${idx}`}
                            data-testid={`med-name-${idx}`}
                          />
                          <datalist id={`med-list-${idx}`}>
                            {getMedSuggestions(med.name).map(m => <option key={m.id} value={m.name} />)}
                          </datalist>
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Dosage</Label>
                        <Input value={med.dosage} onChange={e => updateMed(idx, "dosage", e.target.value)} placeholder="e.g. 500mg" className="mt-1 border-slate-200 text-sm" data-testid={`med-dosage-${idx}`} />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Frequency</Label>
                        <select value={med.frequency} onChange={e => updateMed(idx, "frequency", e.target.value)} className="mt-1 w-full border border-slate-200 rounded-md px-2 py-2 text-xs bg-white">
                          {FREQUENCIES.map(f => <option key={f}>{f}</option>)}
                        </select>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Duration</Label>
                        <select value={med.duration} onChange={e => updateMed(idx, "duration", e.target.value)} className="mt-1 w-full border border-slate-200 rounded-md px-2 py-2 text-xs bg-white">
                          {DURATIONS.map(d => <option key={d}>{d}</option>)}
                        </select>
                      </div>
                      <div className="col-span-2 sm:col-span-3">
                        <Label className="text-xs text-slate-500">Instructions</Label>
                        <Input value={med.instructions} onChange={e => updateMed(idx, "instructions", e.target.value)} placeholder="e.g. After meals, with warm water" className="mt-1 border-slate-200 text-sm" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-sm text-slate-700">General Instructions</Label>
              <textarea value={form.instructions} onChange={e => setForm({ ...form, instructions: e.target.value })} rows={2} placeholder="Rest, diet instructions, etc." className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500/50" />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800 text-white gap-2" disabled={saving} data-testid="save-rx-btn">
                <ClipboardList size={16} /> {saving ? "Saving..." : "Create Prescription"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Prescription Modal */}
      <Dialog open={!!viewRx} onOpenChange={() => setViewRx(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-sans">{viewRx?.rx_number}</DialogTitle>
          </DialogHeader>
          {viewRx && (
            <div className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><p className="text-xs text-slate-500">Patient</p><p className="font-medium">{viewRx.patient_name}</p></div>
                <div><p className="text-xs text-slate-500">Doctor</p><p className="font-medium">{formatDoctorName(viewRx.doctor_name)}</p></div>
                <div className="col-span-2"><p className="text-xs text-slate-500">Diagnosis</p><p className="font-medium">{viewRx.diagnosis}</p></div>
              </div>
              <div className="space-y-2">
                {viewRx.medicines?.map((m, i) => (
                  <div key={i} className="bg-slate-50 rounded-lg p-3">
                    <p className="font-semibold text-sm">{i + 1}. {m.name} — {m.dosage}</p>
                    <p className="text-xs text-slate-500">{m.frequency} × {m.duration} {m.instructions ? `• ${m.instructions}` : ""}</p>
                  </div>
                ))}
              </div>
              {viewRx.instructions && <p className="text-sm text-slate-700 bg-amber-50 px-3 py-2 rounded-lg">{viewRx.instructions}</p>}
              {viewRx.follow_up_date && <p className="text-sm text-emerald-700">Follow-up: {viewRx.follow_up_date}</p>}
              <Button className="w-full gap-2 bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => printPrescription(viewRx)} data-testid="print-rx-modal-btn">
                <Printer size={16} /> Print Prescription
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
